import { ArrowBack, ArrowForward, VisibilityOff } from '@mui/icons-material';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  IconButton,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import {
  DataGrid,
  deDE,
  type GridColDef,
  type GridRowParams,
} from '@mui/x-data-grid';
import {
  useEffect,
  useMemo,
  useState,
  type Dispatch,
  type SetStateAction,
} from 'react';
import { PdfDocument } from '../../Document/PdfDocument';
import type { IDocumentDirectory } from '../../Interfaces/IDocumentDirectory';
import type { IDocumentDirectoryHasDocument } from '../../Interfaces/IDocumentDirectoryHasDocument';
import {
  getEmptyFSSelectObject,
  type IFSScanResult,
  type IFSSelectObject,
} from '../../Interfaces/IFSDirectory';
import type { IFSDirectorySortedDocument } from '../../Interfaces/IFSDirectorySortedDocument';
import type { ILegalRequirementState } from '../../Interfaces/ILegalRequirementState';
import type {
  ILegalTransaction,
  ILegalTransactionFullObject,
} from '../../Interfaces/ILegalTransaction';
import type { ILegalTransactionRecordNumber } from '../../Interfaces/ILegalTransactionRecordNumber';
import type { ILegalTransactionTransactionNumber } from '../../Interfaces/ILegalTransactionTransactionNumber';
import { useAlert } from '../../context/AlertContext';
import { LoadingSpinner } from '../../core/LoadingSpinner';
import {
  useGetFSDocument,
  useGetFSSortedDocumentsWithFilter,
  useSortFSDirectoryDocument,
} from '../../hooks/reactQuerys/DocumentQueries';
import {
  useGetLegalTransactionFullObject,
  useUpdateLegalTransactionFullObject,
} from '../../hooks/reactQuerys/LegalTransactionQueries';
import { DocumentCard } from './DocumentCard';
import { LegalTransactionSelect } from './LegalTransactionSelect';

type IProps = {
  open: boolean;
  onClose: () => void;
  fsScanResult: IFSScanResult;
  legalTransactions: ILegalTransaction[];
  recordNumbers: ILegalTransactionRecordNumber[];
  transactionNumbers: ILegalTransactionTransactionNumber[];
  legalReqStates: ILegalRequirementState[];
  fsDirSortedDocuments: IFSDirectorySortedDocument[];
  onGoToNextDocument: () => void;
  onGoToPreviousDocument: () => void;
  fsScanResults: IFSScanResult[];
  setFsScanResults: Dispatch<SetStateAction<IFSScanResult[]>>;
};

export const columns: GridColDef[] = [
  { field: 'idLegalTransaction', headerName: 'Nr.', flex: 1 },
  { field: 'Title', headerName: 'Bezeichnung', flex: 3 },
  { field: 'recordNumbers', headerName: 'Urkundennummern', flex: 2 },
  { field: 'transactionNumbers', headerName: 'Vorgangsnummern', flex: 2 },
  { field: 'LegalTransactionType', headerName: 'Art', flex: 3 },
  { field: 'LegalTransactionProgress', headerName: 'Phase', flex: 2 },
  { field: 'LegalTransactionState', headerName: 'Status', flex: 2 },
];

export const DialogLayout = ({
  children,
  open,
  onClose,
}: {
  children: React.ReactNode;
  open: boolean;
  onClose: () => void;
}) => {
  return (
    <Dialog open={open} onClose={onClose} maxWidth='xl' fullWidth>
      {children}
    </Dialog>
  );
};

export const DocumentSortingDialog = ({
  open,
  onClose,
  fsScanResult,
  legalTransactions,
  recordNumbers,
  transactionNumbers,
  legalReqStates,
  fsScanResults,
  fsDirSortedDocuments,
  onGoToNextDocument,
  onGoToPreviousDocument,
  setFsScanResults,
}: IProps) => {
  const theme = useTheme();
  const { showAlert } = useAlert();

  const {
    updateLegalTransactionFullObject,
    isPendingUpdateLegalTransactionFullObject,
  } = useUpdateLegalTransactionFullObject();

  const { sortFSDirectoryDocument, isSortingFSDirectoryDocument } =
    useSortFSDirectoryDocument();

  const { refetchFSSortedDocuments } =
    useGetFSSortedDocumentsWithFilter(fsScanResults);

  const { fsDocument, isLoadingFSDocument } = useGetFSDocument(fsScanResult);

  const [searchTerm, setSearchTerm] = useState('');
  const [currentLegalTransactionId, setCurrentLegalTransactionId] = useState<
    number | undefined
  >(undefined);

  const [selectObject, setSelectObject] = useState<IFSSelectObject>(
    getEmptyFSSelectObject(fsScanResult)
  );

  const [currentLegalTransaction, setCurrentLegalTransaction] =
    useState<ILegalTransactionFullObject | null>(null);

  // Only used to load the data. Afterwards, this state is manage by the useState hook above
  const {
    legalTransactionFullObject,
    isFetchingLegalTransactionFullObject,
    refetchLegalTransactionFullObject,
  } = useGetLegalTransactionFullObject(currentLegalTransactionId);

  const [numPages, setNumPages] = useState(1);

  useEffect(() => {
    // This is a workaround, because we need to load the data first, before we can set the state
    if (legalTransactionFullObject) {
      setCurrentLegalTransaction(legalTransactionFullObject);
    }
  }, [legalTransactionFullObject]);

  const fsDirSortedDoc = fsDirSortedDocuments.find(
    fsSortedDoc =>
      fsSortedDoc.fileName === fsScanResult.fileName &&
      fsSortedDoc.idFSDirectory === fsScanResult.idFSDirectory
  );

  const hasUnsortedDocuments = fsScanResults.some(
    singleFsScanResult =>
      singleFsScanResult.path !== fsScanResult.path &&
      !singleFsScanResult.isSorted
  );

  const legalTransactionWithTransactionAndRecordNumbers = useMemo(() => {
    if (!legalTransactions || !transactionNumbers || !recordNumbers) {
      return [];
    }

    return legalTransactions.map(legalTransaction => ({
      ...legalTransaction,
      transactionNumbers: transactionNumbers
        .filter(
          transactionNumber =>
            transactionNumber.idLegalTransaction ===
            legalTransaction.idLegalTransaction
        )
        .map(transactionNumber => transactionNumber.TransactionNumberEntry),
      recordNumbers: recordNumbers
        .filter(
          recordNumber =>
            recordNumber.idLegalTransaction ===
            legalTransaction.idLegalTransaction
        )
        .map(recordNumber => recordNumber.RecordNumberEntry),
    }));
  }, [legalTransactions, transactionNumbers, recordNumbers]);

  const filteredLegalTransactions = useMemo(() => {
    if (searchTerm === '') {
      return legalTransactionWithTransactionAndRecordNumbers;
    }

    const lowerCaseSearchTerm = searchTerm.toLocaleLowerCase();

    return legalTransactionWithTransactionAndRecordNumbers.filter(
      legalTransaction =>
        legalTransaction.Title?.toLocaleLowerCase().includes(
          lowerCaseSearchTerm
        ) ||
        legalTransaction.idLegalTransaction
          .toString()
          .includes(lowerCaseSearchTerm) ||
        legalTransaction.transactionNumbers.some(transactionNumber =>
          transactionNumber.toLocaleLowerCase().includes(lowerCaseSearchTerm)
        ) ||
        legalTransaction.recordNumbers.some(recordNumber =>
          recordNumber.toLocaleLowerCase().includes(lowerCaseSearchTerm)
        )
    );
  }, [legalTransactionWithTransactionAndRecordNumbers, searchTerm]);

  const handleGoToOtherDocument = () => {
    setCurrentLegalTransaction(null);
    setCurrentLegalTransactionId(undefined);
    setSelectObject(getEmptyFSSelectObject(fsScanResult));
  };

  const handleChangeId = (params: GridRowParams<ILegalTransaction>) => {
    if (params.row.idLegalTransaction === currentLegalTransactionId) {
      return;
    }

    setCurrentLegalTransaction(null);
    setCurrentLegalTransactionId(params.row.idLegalTransaction);
    setSelectObject(getEmptyFSSelectObject(fsScanResult));
  };

  const handleAssignDocument = async () => {
    if (!currentLegalTransaction || !fsDocument) {
      return;
    }

    const updatedLegalTransaction: ILegalTransactionFullObject = {
      ...currentLegalTransaction,
      UserInformed_LackOfActivity: Boolean(
        currentLegalTransaction.UserInformed_LackOfActivity
      ),
    };

    if (selectObject.section === 'legalRequirements') {
      updatedLegalTransaction.LegalPhaseArray =
        updatedLegalTransaction.LegalPhaseArray.map(phase => {
          const foundReq = phase.LegalRequirementArray.find(
            legalRequirement =>
              legalRequirement.idLegalRequirement ===
              selectObject.idLegalRequirement
          );
          if (!foundReq) {
            return phase;
          } else {
            if (foundReq.DocumentArrayEmployees !== undefined) {
              foundReq.DocumentArrayEmployees = [
                ...foundReq.DocumentArrayEmployees,
                fsDocument,
              ];
            }
            return {
              ...phase,
              LegalRequirementArray: phase.LegalRequirementArray.map(
                legalRequirement =>
                  legalRequirement.idLegalRequirement ===
                  foundReq!.idLegalRequirement
                    ? foundReq!
                    : legalRequirement
              ),
            };
          }
        });
    } else if (selectObject.section === 'certificate') {
      updatedLegalTransaction.CertificateArray = [
        ...updatedLegalTransaction.CertificateArray,
        fsDocument,
      ];
    } else if (selectObject.section === 'invoice') {
      updatedLegalTransaction.InvoiceArray = [
        ...updatedLegalTransaction.InvoiceArray,
        fsDocument,
      ];
    } else if (selectObject.section === 'dms') {
      const dataOnDirectory: IDocumentDirectoryHasDocument = {
        ...fsDocument,
        idDocumentDirectory: selectObject.idDocumentDirectory,
        idLegalTransaction: updatedLegalTransaction.idLegalTransaction,
      };
      updatedLegalTransaction.DocumentArray = [
        ...updatedLegalTransaction.DocumentArray,
        dataOnDirectory,
      ];
      if (selectObject.idDocumentDirectory < 0) {
        const newDocumentDirectory: IDocumentDirectory = {
          idDocumentDirectory: selectObject.idDocumentDirectory,
          idLegalTransaction: updatedLegalTransaction.idLegalTransaction,
          Parent_idDocumentDirectory: null,
          Title: 'Sortiert über Posteingang',
          Created_at: null,
        };
        updatedLegalTransaction.DocumentDirectoryArray = [
          ...updatedLegalTransaction.DocumentDirectoryArray,
          newDocumentDirectory,
        ];
      }
    }

    try {
      await updateLegalTransactionFullObject(updatedLegalTransaction);

      const sortedDocument: IFSDirectorySortedDocument = {
        idFSDirectorySortedDocument: -1,
        fileName: fsScanResult.fileName,
        idFSDirectory: fsScanResult.idFSDirectory,
        LegalTransactionSortedDoc_idFSDirectorySortedDoc:
          updatedLegalTransaction.idLegalTransaction,
        sorted_at: new Date().toISOString(),
      };

      await sortFSDirectoryDocument(sortedDocument);

      showAlert({ text: 'Dokument erfolgreich zugeordnet' });

      refetchFSSortedDocuments();
      refetchLegalTransactionFullObject();

      setFsScanResults(prev =>
        prev.map(singleFsScanResult =>
          singleFsScanResult.idFSDirectory === fsScanResult.idFSDirectory &&
          singleFsScanResult.fileName === fsScanResult.fileName &&
          singleFsScanResult.path === fsScanResult.path
            ? {
                ...fsScanResult,
                isSorted: true,
              }
            : singleFsScanResult
        )
      );
    } catch {
      showAlert({
        text: 'Fehler beim Zuordnen des Dokumentes',
        severity: 'error',
      });
    }
  };

  const handleHideDocument = async () => {
    const assignedLegalTransaction = legalTransactions.find(
      lT =>
        lT.idLegalTransaction ===
        fsDirSortedDoc?.LegalTransactionSortedDoc_idFSDirectorySortedDoc
    );

    const sortedDocument: IFSDirectorySortedDocument = {
      idFSDirectorySortedDocument: -1,
      fileName: fsScanResult.fileName,
      idFSDirectory: fsScanResult.idFSDirectory,
      LegalTransactionSortedDoc_idFSDirectorySortedDoc:
        assignedLegalTransaction?.idLegalTransaction,
      is_hidden: new Date().toISOString(),
    };

    try {
      await sortFSDirectoryDocument(sortedDocument);

      setFsScanResults(prev =>
        prev.filter(
          singleFsScanResult =>
            singleFsScanResult.idFSDirectory !== fsScanResult.idFSDirectory ||
            singleFsScanResult.fileName !== fsScanResult.fileName
        )
      );

      onGoToNextDocument();

      showAlert({
        text: 'Dokument ausgeblendet',
      });
    } catch {
      showAlert({
        text: 'Fehler beim Ausblenden des Dokuments',
        severity: 'error',
      });
    }
  };

  return (
    <DialogLayout open={open} onClose={onClose}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          borderBottom: `1px solid ${theme.palette.divider}`,
          px: 3,
          py: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant='h5'>
            Dokument zuordnen - {fsScanResult.fileName}
          </Typography>
          <Button
            variant='outlined'
            size='small'
            startIcon={<ArrowBack />}
            disabled={!hasUnsortedDocuments}
            onClick={() => {
              handleGoToOtherDocument();
              onGoToPreviousDocument();
            }}
          >
            Vorheriges Dokument
          </Button>
          <Button
            variant='outlined'
            size='small'
            endIcon={<ArrowForward />}
            disabled={!hasUnsortedDocuments}
            onClick={() => {
              handleGoToOtherDocument();
              onGoToNextDocument();
            }}
          >
            Nächstes Dokument
          </Button>
        </Box>

        <Box>
          {!fsScanResult.isSorted && (
            <Tooltip title='Dokument ausblenden'>
              <IconButton onClick={handleHideDocument}>
                {isSortingFSDirectoryDocument ? (
                  <CircularProgress size={20} />
                ) : (
                  <VisibilityOff />
                )}
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      <DialogContent>
        <Grid container>
          <Grid item xs={12}>
            <Grid container spacing={4}>
              <Grid item xs={5}>
                <Box
                  sx={{
                    border: '1px solid #e0e0e0',
                    borderRadius: 2,
                  }}
                >
                  <Box
                    sx={{
                      height: '45vh',
                      p: 2,
                      overflowY: 'auto',
                      overflowX: 'hidden',
                    }}
                  >
                    {isLoadingFSDocument ? (
                      <LoadingSpinner height='100%' />
                    ) : fsDocument ? (
                      <PdfDocument
                        pdfFile={`data:application/pdf;headers=filename%3D${fsDocument.FileName};base64,${fsDocument.Data}`}
                        numPages={numPages}
                        pageNumber={1}
                        scale={1}
                        setNumPages={setNumPages}
                        scrollable
                      />
                    ) : (
                      <Typography variant='body2' color='text.secondary'>
                        Dokument konnte nicht geladen werden
                      </Typography>
                    )}
                  </Box>
                </Box>
                <Box>
                  <Box sx={{ mt: 2, p: 1 }}>
                    <Typography variant='h6' gutterBottom>
                      Zugehörige Urkunden
                    </Typography>
                    {isFetchingLegalTransactionFullObject ? (
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          height: '20vh',
                          p: 2,
                        }}
                      >
                        <CircularProgress size={24} />
                      </Box>
                    ) : currentLegalTransaction?.CertificateArray &&
                      currentLegalTransaction.CertificateArray.length > 0 ? (
                      <Grid container spacing={2}>
                        {currentLegalTransaction.CertificateArray.filter(
                          certificate =>
                            certificate.FileName.toLowerCase().endsWith('.pdf')
                        ).map((certificate, index) => (
                          <Grid item xs={4} key={index}>
                            <DocumentCard initialDocument={certificate} />
                          </Grid>
                        ))}
                      </Grid>
                    ) : (
                      <Typography variant='body2' color='text.secondary'>
                        Keine Urkunden vorhanden
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={7}>
                <TextField
                  label='Suche'
                  size='small'
                  fullWidth
                  value={searchTerm}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    setSearchTerm(event.target.value);
                  }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
                <Box sx={{ height: 350, mt: 2 }}>
                  <DataGrid
                    localeText={
                      deDE.components.MuiDataGrid.defaultProps.localeText
                    }
                    rowHeight={25}
                    columnHeaderHeight={36}
                    rows={filteredLegalTransactions}
                    columns={columns}
                    getRowId={row => row.idLegalTransaction}
                    onRowClick={params => handleChangeId(params)}
                  />
                </Box>
                <LegalTransactionSelect
                  recordNumbers={recordNumbers}
                  transactionNumbers={transactionNumbers}
                  selectObject={selectObject}
                  setSelectObject={setSelectObject}
                  legalReqStates={legalReqStates}
                  isLoadingCurrentLegalTransaction={
                    isFetchingLegalTransactionFullObject
                  }
                  currentLegalTransaction={currentLegalTransaction}
                  setCurrentLegalTransaction={setCurrentLegalTransaction}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions
        sx={{
          borderTop: '1px solid #ccc',
          paddingTop: 1,
        }}
      >
        <Button variant='outlined' onClick={onClose}>
          Abbrechen
        </Button>
        <Button
          variant='contained'
          disabled={
            (selectObject.section === 'legalRequirements' &&
              selectObject.idLegalRequirement < 0) ||
            isPendingUpdateLegalTransactionFullObject
          }
          onClick={handleAssignDocument}
        >
          {isPendingUpdateLegalTransactionFullObject ? (
            <Box display='flex' alignItems='center' gap={1}>
              <span>Hinzufügen</span>
              <CircularProgress size={20} color='inherit' />
            </Box>
          ) : (
            'Hinzufügen'
          )}
        </Button>
      </DialogActions>
    </DialogLayout>
  );
};
